/**
 * titleHistory — the viewer's own record of what a thread has been called
 * (2026-09-09, Miles: a rename is personal and reflects everywhere within the
 * user's account; former names are saved for SEARCH only). Every name change
 * on the viewer's index doc — a rename, or an auto label replacing an older
 * one — appends here, oldest first, capped; nothing renders it except a
 * search hit's "was “…”".
 */
import { THREAD_TITLE_HISTORY_MAX, type ThreadTitleHistoryEntry } from '../../components/GlobalChatSystem/types';

/**
 * The history with the title about to be replaced appended as the previous
 * name of the new one. Unchanged (same reference) when nothing changes.
 */
export function appendFormerTitle(
  history: readonly ThreadTitleHistoryEntry[] | null | undefined,
  previousTitle: string | null | undefined,
  nextTitle: string | null | undefined,
  at: number = Date.now(),
): ThreadTitleHistoryEntry[] {
  const list = Array.isArray(history) ? [...history] : [];
  const prev = (previousTitle ?? '').trim();
  const next = (nextTitle ?? '').trim();
  if (!next || !prev || prev === next) return Array.isArray(history) ? (history as ThreadTitleHistoryEntry[]) : list;
  list.push({ title: next, previousTitle: prev, at });
  return list.length > THREAD_TITLE_HISTORY_MAX ? list.slice(list.length - THREAD_TITLE_HISTORY_MAX) : list;
}

/**
 * The name a user-renamed thread was last renamed TO, from its own history.
 *
 * THE HISTORY IS THE AUTHORITY FOR A RENAMED THREAD (2026-09-11). A rename
 * writes two facts in one go — the `title` field and a history entry naming it
 * — and the entry is append-only while the field is overwritable by anything
 * that mirrors a row. When they disagree on a `userRenamed` row, the field is
 * the one that lost: something replaced the name the person chose without
 * recording a rename.
 *
 * That is not hypothetical. On 2026-09-11 a thread renamed to "Zebracorn
 * invoices" was found stored as `{ title: "prod-e2e thread-experience: the
 * zebracorn…", userRenamed: true, titleHistory: [{ title: "Zebracorn
 * invoices", … }] }` — the auto label back on top of the rename, with the
 * history still holding the truth. Reading the history back is what makes the
 * name return for every row already in that state, not only for rows renamed
 * from here on.
 *
 * Only ever consulted for `userRenamed` rows: the auto-labeler appends history
 * too, and on a thread nobody has named, its latest label IS the title.
 */
export function renamedTitleFromHistory(
  history: readonly ThreadTitleHistoryEntry[] | null | undefined,
): string | null {
  if (!Array.isArray(history) || history.length === 0) return null;
  const newest = history[history.length - 1];
  const title = (newest?.title ?? '').trim();
  return title || null;
}

/**
 * The name to show for a row: the stored title, unless a rename says otherwise.
 * Identity for every row that is not user-renamed, and for every renamed row
 * whose title and history already agree — which is all of them, once nothing
 * is overwriting renames any more.
 */
export function authoritativeThreadTitle(
  row: { title?: string | null; userRenamed?: boolean | null; titleHistory?: readonly ThreadTitleHistoryEntry[] | null },
): string {
  const title = (row?.title ?? '').trim();
  if (row?.userRenamed !== true) return title;
  const renamed = renamedTitleFromHistory(row?.titleHistory);
  return renamed || title;
}

/** Every former name a record has carried (titles and previous titles), minus the current one. */
export function formerTitlesOf(
  history: readonly ThreadTitleHistoryEntry[] | null | undefined,
  currentTitle: string | null | undefined,
): string[] {
  const current = (currentTitle ?? '').trim();
  const out: string[] = [];
  for (const h of Array.isArray(history) ? history : []) {
    for (const candidate of [h?.title, h?.previousTitle]) {
      const t = typeof candidate === 'string' ? candidate.trim() : '';
      if (t && t !== current && !out.includes(t)) out.push(t);
    }
  }
  return out;
}
